/**
 * Audit feature — Zod schemas.
 *
 * Audit events are append-only log entries the backend emits for
 * every security-relevant action. They are the canonical
 * compliance record and are frequently exported, so field names
 * match the backend column names exactly — do not rename in
 * transit.
 */

import { z } from "zod";
import {
  auditListResponseSchema,
  auditPaginationSchema,
  auditVerifyResultSchema,
  platformAuditEventSchema,
  platformAuditListResponseSchema,
  tenantAuditEventSchema,
  windowsAuthenticationSchema,
  windowsAuthenticationsResponseSchema,
} from "./api.generated";

export {
  auditListResponseSchema,
  auditVerifyResultSchema,
  platformAuditListResponseSchema,
  windowsAuthenticationSchema,
  windowsAuthenticationsResponseSchema,
};

// ── List filter (URL search params) ───────────────────────────────────────
//
// Every filter lives in the URL so every investigation link is
// deep-linkable and survives refresh. React state never mirrors
// these — the URL is the single source of truth. See
// /CLAUDE.md "URL is the source of truth for list/filter state".

export const auditListSearchSchema = z.object({
  /** Tenant audit-page tab. Platform audit ignores this field. */
  view: z.enum(["events", "windows-authentication", "evidence", "signed-records"]).optional(),
  /** Full-text search across actor email, event name, target id. */
  q: z.string().optional(),
  /** Event name filter, e.g. `user.passkey.created`. Exact match. */
  event: z.string().optional(),
  /**
   * Left-anchored event-name prefix, e.g. `frontline.`. Backs the
   * one-click compliance-evidence timeline: every event in a
   * ceremony family (pair, classify, badge, supervised approval,
   * break-glass) in one attributable, deep-linkable view. The
   * backend matches `action LIKE 'prefix%'` (metacharacters
   * escaped); AND-composes with every other filter.
   */
  event_prefix: z.string().optional(),
  /** Actor email or id fragment. */
  actor: z.string().optional(),
  /**
   * Exact actor UUID — the "all actions BY this identity" pivot.
   * For a service account this is the SA's admin_users.id. The
   * backend expects a UUID and 400s on a malformed value, so
   * deep-links from feature pages should pass a well-formed id.
   */
  actor_id: z.string().uuid().optional(),
  /**
   * Exact resource UUID — the "all actions ON this identity"
   * pivot. For a service account this is the SA's
   * admin_users.id too (target_id on lifecycle events).
   */
  resource_id: z.string().uuid().optional(),
  /**
   * Exact subject UUID — the per-ENTITY pivot (§5d): "everything that
   * ever happened to/by this entity" across its whole lifecycle,
   * regardless of resource_type. For an agent this returns the COMPLETE
   * trail — registry lifecycle, delegations, grants, AND T3 approval
   * decisions — in one query, because every agent emit site stamps the
   * agent as the subject. Distinct from resource_id, which pins the
   * granular resource a single action touched (a grant jti, an approval
   * id). The agent Activity tab pins this.
   */
  subject_id: z.string().uuid().optional(),
  /** Subject-type companion narrow (e.g. "agent"). AND-composes with subject_id. */
  subject_type: z.string().optional(),
  /** Resource type filter (e.g. "credential", "user"). */
  resource_type: z.string().optional(),
  /** Exact IP address (incident response pivot). */
  ip: z.string().optional(),
  /** Exact bearer-session UUID — the SOC "all actions from this session" pivot. */
  session: z.string().optional(),
  /** ISO-8601 start of range (inclusive). */
  from: z.string().optional(),
  /** ISO-8601 end of range (inclusive). */
  to: z.string().optional(),
  /** Result filter. */
  result: z.enum(["success", "failure", "denied"]).optional(),
  /**
   * Windows authentication view only: which population to show. `witnessed` is a sign-in PureID
   * completed itself; `reported` is one an enrolled machine told us about. Absent = both.
   */
  origin: z.enum(["witnessed", "reported"]).optional(),
  /**
   * Windows authentication view only: successes or failures. Deliberately NOT the `result` field
   * above — that one carries `denied` and belongs to the general event list, and a tab switch must
   * not silently reinterpret one filter as the other.
   */
  outcome: z.enum(["success", "failure"]).optional(),
  /**
   * Canonical time-range preset. When set, the view renders as
   * the rolling window (e.g. 24h = now-24h to now). Mutually
   * exclusive with from/to: setting a preset clears the explicit
   * range so the query is unambiguous.
   */
  preset: z.enum(["15m", "1h", "24h", "7d"]).optional(),
  /** Offset-based pagination. */
  offset: z.coerce.number().int().nonnegative().optional(),
  /**
   * Page size. Bounded to the backend's MaxListLimit (200); out-of-range
   * values are clamped rather than rejected so a hand-edited URL still
   * resolves to a valid page. Omitted → backend default (50).
   */
  limit: z.coerce.number().int().min(1).max(200).catch(50).optional(),
});
export type AuditListSearch = z.infer<typeof auditListSearchSchema>;

// ── Rows ──────────────────────────────────────────────────────────────────
//
// The wire shapes are the Audit operations in api/openapi/operations.yaml, generated into
// api.generated.ts. Tenant and platform rows differ: a tenant row carries tenant_id and the device /
// geo / organization context, a platform row carries the tenant it touched. The console renders
// both through one row type, so a platform row is widened with `tenant_id: null` where it is fetched.

export type TenantAuditEvent = z.infer<typeof tenantAuditEventSchema>;
export type PlatformAuditEvent = z.infer<typeof platformAuditEventSchema>;

/** One row as the audit table renders it: a tenant row, or a platform row with its target tenant. */
export type AuditEvent = TenantAuditEvent &
  Partial<Pick<PlatformAuditEvent, "target_tenant_id" | "target_tenant_name">>;

export interface AuditListResponse {
  data: AuditEvent[];
  pagination: z.infer<typeof auditPaginationSchema>;
}

export type AuditVerifyResult = z.infer<typeof auditVerifyResultSchema>;

/**
 * One row of the Windows authentication view.
 *
 * TWO TRUST LEVELS SHARE THIS LIST AND `origin` IS WHAT KEEPS THEM APART. A `witnessed` row is a
 * sign-in PureID completed itself and issued a certificate for. A `reported` row is what an
 * enrolled machine, authenticated by its own device key, told us happened on it. Nothing on the
 * server saw the second kind.
 */
export type WindowsAuthentication = z.infer<typeof windowsAuthenticationSchema>;
export type WindowsAuthenticationsResponse = z.infer<typeof windowsAuthenticationsResponseSchema>;
